import { useEffect } from 'react';

type Binding = {
  key: string; // 'Escape' | 'Space' | 'Enter' etc (KeyboardEvent.key or .code)
  ctrl?: boolean;
  meta?: boolean;
  handler: (e: KeyboardEvent) => void;
  /** If true, fire even when a text input is focused. Default false. */
  allowInInputs?: boolean;
};

function isTextTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (t.isContentEditable) return true;
  return false;
}

/**
 * Minimal global hotkey hook — vanilla keydown listener on document.
 * Keeps a stable effect per caller; pass `enabled=false` to disable.
 */
export function useTelecallerHotkeys(bindings: Binding[], enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      for (const b of bindings) {
        // Normalise space — handlers can pass 'Space' (code) or ' ' (key)
        const keyMatch =
          e.key === b.key ||
          e.code === b.key ||
          (b.key === 'Space' && (e.key === ' ' || e.code === 'Space'));
        if (!keyMatch) continue;
        if (b.ctrl && !(e.ctrlKey || e.metaKey)) continue;
        if (b.meta && !e.metaKey) continue;
        if (!b.allowInInputs && isTextTarget(e.target)) continue;
        b.handler(e);
        break;
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [bindings, enabled]);
}
